import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  connect() {
    this.hasUnsavedChanges = false;
    this.originalValues = new Map();
    this.isInitializing = true;

    // Bind methods once to maintain references for event removal
    this.boundOnFormChange = this.onFormChange.bind(this);
    this.boundHandleBeforeUnload = this.handleBeforeUnload.bind(this);
    this.boundHandleTurboBeforeVisit = this.handleTurboBeforeVisit.bind(this);
    this.boundClearUnsavedChanges = this.clearUnsavedChanges.bind(this);

    // Wait for the page to fully load before tracking changes
    setTimeout(() => {
      this.initialize();
    }, 100);
  }

  initialize() {
    // Find all form fields automatically
    this.formFields = this.findFormFields();

    // Store original values for all form fields
    this.formFields.forEach((field) => {
      this.storeOriginalValue(field);
    });

    // Wait a bit more before enabling change tracking
    setTimeout(() => {
      this.isInitializing = false;

      // Listen for form changes
      this.element.addEventListener("input", this.boundOnFormChange);
      this.element.addEventListener("change", this.boundOnFormChange);

      // Listen for beforeunload event (when user tries to leave the page)
      window.addEventListener("beforeunload", this.boundHandleBeforeUnload);

      // Listen for turbo:before-visit (when navigating within the app)
      document.addEventListener(
        "turbo:before-visit",
        this.boundHandleTurboBeforeVisit,
      );

      // Listen for form submission to clear warning
      this.element.addEventListener("submit", this.boundClearUnsavedChanges);
    }, 200);
  }

  disconnect() {
    // Clean up event listeners using the bound references
    this.element.removeEventListener("input", this.boundOnFormChange);
    this.element.removeEventListener("change", this.boundOnFormChange);
    window.removeEventListener("beforeunload", this.boundHandleBeforeUnload);
    document.removeEventListener(
      "turbo:before-visit",
      this.boundHandleTurboBeforeVisit,
    );
    this.element.removeEventListener("submit", this.boundClearUnsavedChanges);
  }

  findFormFields() {
    // Find all input, textarea, and select elements within the form
    const selector = "input, textarea, select";
    const fields = Array.from(this.element.querySelectorAll(selector));

    // Filter out hidden fields we don't want to track (like Rails CSRF tokens, etc.)
    return fields.filter((field) => {
      // Skip hidden fields with Rails-specific names
      if (
        field.type === "hidden" &&
        (field.name === "authenticity_token" ||
          field.name === "_method" ||
          field.name === "utf8" ||
          field.name === "user_timezone_offset" || // Skip timezone offset from datetime-local controller
          field.name.includes("[_destroy]")) // Skip destroy flags for nested forms
      ) {
        return false;
      }

      // Skip fields that are disabled or readonly
      if (field.disabled || field.readOnly) {
        return false;
      }

      return true;
    });
  }

  storeOriginalValue(field) {
    let value;

    if (field.type === "checkbox" || field.type === "radio") {
      value = field.checked;
    } else if (field.type === "file") {
      // For file inputs, store the current number of files
      value = field.files.length;
    } else {
      value = field.value.trim(); // Store trimmed values to avoid whitespace issues
    }

    this.originalValues.set(field, value);
  }

  onFormChange(event) {
    // Skip if we're still initializing
    if (this.isInitializing) {
      return;
    }

    const field = event.target;

    // Skip if this field is not tracked
    if (!this.originalValues.has(field)) {
      return;
    }

    // Debounce the change detection to avoid rapid fire events
    clearTimeout(this.changeTimeout);
    this.changeTimeout = setTimeout(() => {
      this.checkFormChanges();
    }, 50);
  }

  checkFormChanges() {
    // Check if any field has been changed
    this.hasUnsavedChanges = this.formFields.some((field) => {
      const originalValue = this.originalValues.get(field);
      const currentValue = this.getCurrentValue(field);

      return this.valuesAreDifferent(originalValue, currentValue, field);
    });
  }

  getCurrentValue(field) {
    if (field.type === "checkbox" || field.type === "radio") {
      return field.checked;
    } else if (field.type === "file") {
      // For file fields, we consider them changed only if files are selected
      // and it wasn't already a file field with files
      return field.files.length;
    } else {
      return field.value.trim(); // Trim whitespace to avoid false positives
    }
  }

  valuesAreDifferent(originalValue, currentValue, field) {
    // Handle file inputs specially
    if (field.type === "file") {
      // If originally there were no files and now there are files, it's a change
      // If originally there were files and now there are different number of files, it's a change
      return originalValue !== currentValue;
    }

    // Handle string values (trim to avoid whitespace differences)
    if (typeof originalValue === "string" && typeof currentValue === "string") {
      return originalValue.trim() !== currentValue.trim();
    }

    // Handle other types
    return originalValue !== currentValue;
  }

  handleBeforeUnload(event) {
    if (this.hasUnsavedChanges) {
      const message =
        "У вас есть несохраненные изменения. Вы уверены, что хотите покинуть страницу?";
      event.preventDefault();
      event.returnValue = message;
      return message;
    }
  }

  handleTurboBeforeVisit(event) {
    if (this.hasUnsavedChanges) {
      const confirmed = confirm(
        "У вас есть несохраненные изменения. Вы уверены, что хотите покинуть страницу?",
      );
      if (!confirmed) {
        event.preventDefault();
      }
    }
  }

  clearUnsavedChanges() {
    this.hasUnsavedChanges = false;

    // Also clear any pending timeouts
    clearTimeout(this.changeTimeout);
  }

  // Public method to manually check for changes (can be called from other controllers)
  checkForChanges() {
    this.formFields.forEach((field) => {
      const event = new Event("change", { bubbles: true });
      field.dispatchEvent(event);
    });
  }

  // Method to re-scan for new fields (useful when dynamic content is added)
  refreshFields() {
    this.formFields = this.findFormFields();

    // Store original values for any new fields
    this.formFields.forEach((field) => {
      if (!this.originalValues.has(field)) {
        this.storeOriginalValue(field);
      }
    });

    // Trigger a check for changes after adding new fields
    setTimeout(() => {
      this.checkFormChanges();
    }, 100);
  }

  // Method to reset the form state (useful for programmatic changes)
  resetFormState() {
    this.originalValues.clear();
    this.hasUnsavedChanges = false;

    this.formFields.forEach((field) => {
      this.storeOriginalValue(field);
    });
  }
}
